import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, MaskLines } from "./Reveal";
import { Crown, History, ShieldCheck } from "lucide-react";
import { IMAGES } from "@/constants/images";

const PILLARS = [
  {
    icon: History,
    title: "In It For The Long Haul",
    body: "Shabz didn't appear last cycle. She's traded through bull runs, brutal bear markets and crypto winters — and she's still here, still profitable, still teaching.",
  },
  {
    icon: Crown,
    title: "Trading Royalty",
    body: "In a space overrun by overnight gurus, longevity is the ultimate flex. The people who actually move markets treat Shabz as royalty because she earned it the hard way.",
  },
  {
    icon: ShieldCheck,
    title: "Respected In Bitcoin",
    body: "From equities and options to Bitcoin and crypto, the insiders who've been around the block respect her more than almost anyone. That trust isn't bought — it's built over years.",
  },
];

export const Authority = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section ref={ref} className="relative overflow-hidden border-t border-white/10 py-24 sm:py-32">
      <motion.div style={{ y }} className="absolute inset-0 -z-10 scale-110">
        <img
          src={IMAGES.bitcoin}
          alt="Gold Bitcoin coins and a crown — trading royalty"
          className="h-full w-full object-cover object-center opacity-30"
          data-testid="authority-bg"
        />
      </motion.div>
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#0a0a0b] via-[#0a0a0b]/85 to-[#0a0a0b]" />

      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
        <div className="mx-auto max-w-3xl text-center">
          <Reveal>
            <span className="inline-flex items-center gap-3 font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.3em] text-[#d4af37]">
              <Crown className="h-4 w-4" strokeWidth={1.6} /> Trading Royalty
            </span>
          </Reveal>
          <h2 className="mt-8 font-['Playfair_Display'] text-3xl font-medium tracking-tight sm:text-4xl lg:text-5xl">
            <MaskLines whileInView lineClass="gold-metallic-text" lines={["Respected by the people", "who move Bitcoin."]} />
          </h2>
          <Reveal delay={0.15}>
            <p className="mt-7 text-base leading-relaxed text-[#c9c9c2] sm:text-lg">
              Anyone can post a screenshot of a good week. Very few are still standing a decade of
              cycles later. Shabz Fazl has been in the markets for the long haul &mdash; and that
              staying power is exactly why the most respected names in Bitcoin and trading treat her
              as royalty.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-px overflow-hidden border border-white/10 bg-white/10 md:grid-cols-3">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1}>
              <div
                data-testid={`authority-pillar-${i}`}
                className="gold-toptrim flex h-full flex-col gap-5 bg-[#0e0e10]/90 p-9 backdrop-blur-sm transition-colors duration-300 hover:bg-[#141416]"
              >
                <p.icon className="h-7 w-7 text-[#d4af37]" strokeWidth={1.3} />
                <h3 className="font-['Playfair_Display'] text-xl text-[#f5f5f0]">{p.title}</h3>
                <p className="text-sm leading-relaxed text-[#a1a1aa]">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
